/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 7b: who follows a program.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconSearch } from "@tabler/icons-react";
import type { GetProgrammingSubscribersResponse } from "@lemonadetraining/contracts";
import { Alert, AlertDescription, Button, Input, Label, Skeleton } from "@lemonadetraining/web-ui";
import { getProgramMembers } from "@lemonadetraining/web-shared";
import { isPermissionDenied } from "../../lib/callableError";
import { membersLabel } from "../../lib/programLabels";

export interface ProgramMembersTabProps {
  programmingUID: string;
  memberCount: number;
}

type Load =
  | { status: "loading" }
  | { status: "error"; denied: boolean }
  | { status: "ready"; data: GetProgrammingSubscribersResponse };

const SEARCH_FROM = 10;

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "?";

export function ProgramMembersTab({ programmingUID, memberCount }: ProgramMembersTabProps) {
  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [search, setSearch] = useState("");
  const latest = useRef(0);

  const fetchMembers = useCallback(async () => {
    const request = ++latest.current;
    setLoad({ status: "loading" });
    try {
      const raw: unknown = await getProgramMembers(programmingUID);
      if (request !== latest.current) return;
      // A function deployed before `withTotal` answers with the plain list.
      const data: GetProgrammingSubscribersResponse | null = Array.isArray(raw)
        ? { subscribers: raw, total: raw.length }
        : (raw as GetProgrammingSubscribersResponse | null);
      if (!data || !Array.isArray(data.subscribers) || typeof data.total !== "number") {
        throw new Error("unexpected members response");
      }
      setLoad({ status: "ready", data });
    } catch (error: unknown) {
      if (request !== latest.current) return;
      setLoad({ status: "error", denied: isPermissionDenied(error) });
    }
  }, [programmingUID]);

  useEffect(() => {
    setSearch("");
    void fetchMembers();
  }, [fetchMembers]);

  const shown = useMemo(() => {
    if (load.status !== "ready") return [];
    const needle = search.trim().toLowerCase();
    const rows = load.data.subscribers;
    return needle ? rows.filter((row) => row.name.toLowerCase().includes(needle)) : rows;
  }, [load, search]);

  if (load.status === "loading") {
    const rows = Math.min(Math.max(memberCount, 1), 6);
    return (
      <div className="flex flex-col gap-3" data-testid="program-members-loading" aria-busy="true">
        <span className="sr-only">Loading members</span>
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="size-9 rounded-full" />
            <Skeleton className="h-4 w-40" />
          </div>
        ))}
      </div>
    );
  }

  if (load.status === "error") {
    return load.denied ? (
      <Alert data-testid="program-members-denied">
        <AlertDescription>
          Only the coach who created this program and the gym's admins can see who follows it.
        </AlertDescription>
      </Alert>
    ) : (
      <Alert variant="destructive" data-testid="program-members-error">
        <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
          <span>Couldn't load the members. Check your connection and try again.</span>
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            onClick={() => void fetchMembers()}
          >
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const { subscribers, total } = load.data;

  if (total === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="program-members-empty">
        No members yet. When someone joins, they show up here.
      </p>
    );
  }

  const capped = subscribers.length < total;

  return (
    <section className="flex flex-col gap-4" aria-labelledby="program-members-heading">
      <div className="flex flex-col gap-1">
        <h2 id="program-members-heading" className="text-base font-semibold">
          {membersLabel(total)}
        </h2>
        {capped && (
          <p className="text-sm text-muted-foreground" data-testid="program-members-capped">
            Showing {subscribers.length} of {total}.
            {subscribers.length > SEARCH_FROM && " Search looks through these."}
          </p>
        )}
      </div>

      {subscribers.length > SEARCH_FROM && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="program-members-search" className="sr-only">
            Search members
          </Label>
          <div className="relative max-w-sm">
            <IconSearch
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id="program-members-search"
              type="search"
              placeholder="Search by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="min-h-11 pl-9"
            />
          </div>
        </div>
      )}

      {shown.length === 0 ? (
        <p className="text-sm text-muted-foreground" role="status">
          No member matches “{search.trim()}”.
        </p>
      ) : (
        <ul className="flex flex-col" aria-label="Members" data-testid="program-members-list">
          {shown.map((row) => (
            <li
              key={row.uid}
              className="flex min-h-11 items-center gap-3 border-t py-2 first:border-t-0"
            >
              <span
                aria-hidden="true"
                className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground"
              >
                {initials(row.name)}
              </span>
              <span className="min-w-0 break-words text-sm font-medium">{row.name}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default ProgramMembersTab;
